import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Palette, Hand } from "lucide-react";
import { useLightbox } from "./LightboxContext";

const KLEIN = "#0851F2";
const INK = "#0a0a0a";

const works = [
  {
    id: "alien",
    title: "HOODED ALIEN",
    sub: "Sticker design",
    blurb:
      "A peace-sign alien girl in a camo hoodie — illustrated for a sticker pack. Hand-built vector linework with painterly fill textures.",
    img: "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/dyh0f6m7_vanzemaljac.png",
    fit: "contain",
    start: { x: -160, y: -80, r: -7 },
  },
  {
    id: "bubi",
    title: "BUBI & ŠUBI",
    sub: "Children's book cover",
    blurb:
      "Cover artwork for the children's adventure book ‘Priručnik za zlikovce’ — a brave wolf and bunny duo at sundown in the redwood forest.",
    img: "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/vjpy8tth_De%C4%8DijaIlustracija.jpg",
    fit: "cover",
    start: { x: 110, y: -110, r: 6 },
  },
  {
    id: "editorial",
    title: "DISCONNECTED",
    sub: "Editorial illustration",
    blurb:
      "A quietly serious editorial piece on kids today — glued to screens, rarely present with the world. A small robot offers a Lego brick; the child barely notices. Childhood, on pause.",
    img: "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/wwzjet4n_EDITORIJAL%20FINAL.jpg",
    fit: "cover",
    start: { x: -110, y: 90, r: 4 },
  },
  {
    id: "gamer",
    title: "TREASURE FOX",
    sub: "Gaming illustration",
    blurb:
      "Character art for a game project — a wide-eyed fox mascot diving into a ruby cave. Built entirely in vectors for print and screen.",
    img: "/illustration/gamer.jpg",
    fit: "cover",
    start: { x: 160, y: 70, r: -8 },
  },
  {
    id: "exam",
    title: "TOM AND JERRY",
    sub: "Star Wars crossover",
    blurb:
      "What if Tom and Jerry switched roles in another galaxy far, far away? A speculative crossover — Tom becomes the Sith hunter, Jerry the runaway Jedi.",
    img: "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/z3hgz2dk_image.png",
    fit: "cover",
    start: { x: 30, y: 130, r: 2 },
  },
];

export default function DigitalIllustration() {
  const [selectedId, setSelectedId] = useState(works[0].id);
  const [topZ, setTopZ] = useState(() =>
    Object.fromEntries(works.map((w, i) => [w.id, i + 1]))
  );
  const [maxZ, setMaxZ] = useState(works.length);
  const boardRef = useRef(null);
  const { open } = useLightbox();

  const selected = works.find((w) => w.id === selectedId);

  const bringToFront = (id) => {
    const next = maxZ + 1;
    setMaxZ(next);
    setTopZ((s) => ({ ...s, [id]: next }));
  };

  const handleSelect = (id) => {
    setSelectedId(id);
    bringToFront(id);
  };

  return (
    <div className="mb-20 md:mb-28" data-testid="digital-illustration">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14">
        <div className="flex items-center gap-4">
          <span className="w-10 h-10 border-2 border-[var(--ink)] flex items-center justify-center bg-white">
            <Palette className="w-4 h-4" strokeWidth={2} />
          </span>
          <h3 className="font-sans-display uppercase text-3xl md:text-5xl tracking-[-0.01em] text-[var(--ink)]">
            Digital Illustration
          </h3>
        </div>
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--ink-soft)]">
          §03.e — Drag the cards · click to read
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
        {/* Sticker board with grid background */}
        <div className="lg:col-span-7 relative">
          <div
            ref={boardRef}
            className="relative w-full overflow-hidden border-2 border-[var(--ink)] shadow-[8px_8px_0_var(--ink)]"
            style={{
              background: "#F5F2EA",
              backgroundImage:
                "linear-gradient(rgba(10,10,10,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(10,10,10,0.08) 1px, transparent 1px)",
              backgroundSize: "44px 44px",
              height: "min(78vh, 660px)",
              minHeight: 540,
            }}
            data-testid="illustration-board"
          >
            {/* Hint pill */}
            <span className="absolute top-4 right-5 z-50 inline-flex items-center gap-2 px-3 py-1.5 bg-white border-2 border-[var(--ink)] rounded-full font-mono text-[10px] uppercase tracking-[0.2em] shadow-[3px_3px_0_var(--ink)] pointer-events-none">
              <Hand className="w-3.5 h-3.5" /> drag · click
            </span>

            {/* Section index sticker (top-left, bold mono) */}
            <span
              className="absolute top-4 left-5 z-40 inline-flex items-center gap-2 px-3 py-1.5 text-white rounded-full font-mono text-[10px] uppercase tracking-[0.25em] pointer-events-none"
              style={{ background: KLEIN }}
            >
              ★ Illustration · 05 pieces
            </span>

            {/* Sticker cards */}
            {works.map((w, idx) => {
              const isSelected = w.id === selectedId;
              const accent = isSelected ? KLEIN : INK;
              return (
                <motion.div
                  key={w.id}
                  drag
                  dragConstraints={boardRef}
                  dragElastic={0.12}
                  dragMomentum={false}
                  onDragStart={() => bringToFront(w.id)}
                  onClick={() => handleSelect(w.id)}
                  initial={{
                    x: w.start.x,
                    y: w.start.y,
                    rotate: w.start.r,
                  }}
                  animate={{
                    scale: isSelected ? 1.08 : 1,
                  }}
                  whileHover={{ scale: isSelected ? 1.1 : 1.04 }}
                  whileTap={{ scale: isSelected ? 1.06 : 0.97 }}
                  transition={{ type: "spring", stiffness: 240, damping: 22 }}
                  className="absolute top-1/2 left-1/2 cursor-grab active:cursor-grabbing select-none"
                  style={{
                    zIndex: topZ[w.id],
                    transformOrigin: "center",
                  }}
                  data-testid={`polaroid-${w.id}`}
                >
                  <div
                    className="bg-white border-2 border-[var(--ink)]"
                    style={{
                      width: 176,
                      transform: `translate(-50%, -50%)`,
                      boxShadow: isSelected
                        ? `6px 6px 0 ${KLEIN}`
                        : "5px 5px 0 var(--ink)",
                      transition: "box-shadow 200ms ease",
                    }}
                  >
                    {/* Image plate */}
                    <div
                      className="relative w-full overflow-hidden bg-[#ededeb] border-b-2 border-[var(--ink)]"
                      style={{ aspectRatio: "1 / 1" }}
                    >
                      <img
                        src={w.img}
                        alt={w.title}
                        draggable={false}
                        className={`absolute inset-0 w-full h-full ${
                          w.fit === "cover"
                            ? "object-cover"
                            : "object-contain"
                        } pointer-events-none`}
                        loading="lazy"
                      />
                      {/* Index chip */}
                      <span
                        className="absolute top-2 left-2 font-mono text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 bg-white border border-[var(--ink)]"
                      >
                        0{idx + 1}
                      </span>
                    </div>

                    {/* Label band */}
                    <div
                      className="flex items-center justify-between px-2.5 py-2"
                      style={{
                        background: isSelected ? KLEIN : "#FFFFFF",
                        color: isSelected ? "#FFFFFF" : INK,
                      }}
                    >
                      <span
                        className="font-sans-display uppercase text-[12px] leading-none tracking-[0.01em] truncate"
                        title={w.title}
                      >
                        {w.title}
                      </span>
                      <span className="font-mono text-[8px] uppercase tracking-[0.2em] opacity-80 shrink-0 ml-2">
                        ↗
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Detail panel */}
        <div className="lg:col-span-5">
          <div
            className="relative h-full border-2 border-[var(--ink)] bg-white shadow-[8px_8px_0_var(--ink)] p-6 md:p-8 flex flex-col"
            data-testid="illustration-detail"
            style={{ minHeight: 540 }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={selected.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col h-full"
              >
                {/* Big preview */}
                <button
                  type="button"
                  onClick={() => open(selected.img, `${selected.title} — ${selected.sub}`)}
                  aria-label={`Open ${selected.title} fullscreen`}
                  data-testid="illustration-detail-open"
                  className="group block w-full cursor-zoom-in focus:outline-none"
                >
                  <div
                    className="relative w-full bg-[#ededeb] overflow-hidden border-2 border-[var(--ink)]"
                    style={{ aspectRatio: "1 / 1" }}
                  >
                    <img
                      src={selected.img}
                      alt={selected.title}
                      className={`absolute inset-0 w-full h-full ${
                        selected.fit === "cover"
                          ? "object-cover"
                          : "object-contain"
                      } transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]`}
                      loading="lazy"
                    />
                  </div>
                  <span className="mt-2 inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.25em] text-[var(--ink-soft)] group-hover:text-[var(--ink)] transition-colors">
                    Click image to expand ↗
                  </span>
                </button>

                {/* Info */}
                <div className="mt-5">
                  <span
                    className="font-mono text-[10px] uppercase tracking-[0.3em]"
                    style={{ color: KLEIN }}
                  >
                    {selected.sub}
                  </span>
                  <h4 className="mt-2 font-sans-display uppercase text-3xl md:text-4xl leading-[0.95] tracking-[-0.01em] text-[var(--ink)]">
                    {selected.title}
                  </h4>
                  <p className="mt-3 text-sm md:text-base text-[#3a3a3a] leading-relaxed">
                    {selected.blurb}
                  </p>
                </div>

                {/* Tray */}
                <div className="mt-auto pt-6">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ink-soft)] mb-3">
                    All pieces
                  </div>
                  <div className="grid grid-cols-5 gap-2">
                    {works.map((w) => {
                      const active = w.id === selectedId;
                      return (
                        <button
                          key={w.id}
                          type="button"
                          onClick={() => handleSelect(w.id)}
                          data-testid={`tray-${w.id}`}
                          aria-label={`Show ${w.title}`}
                          className="relative aspect-square overflow-hidden border-2 transition-all"
                          style={{
                            borderColor: active ? KLEIN : "var(--ink)",
                            boxShadow: active
                              ? `3px 3px 0 ${KLEIN}`
                              : "3px 3px 0 var(--ink)",
                            transform: active
                              ? "translate(-1px,-1px)"
                              : "none",
                          }}
                        >
                          <img
                            src={w.img}
                            alt={w.title}
                            className={`absolute inset-0 w-full h-full ${
                              w.fit === "cover"
                                ? "object-cover"
                                : "object-contain"
                            } bg-white`}
                            loading="lazy"
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
